import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import PriceTimeline from "./PriceTimeline";
import EmissionTimeline from "./EmissionTimeline";
import {
  CartesianGrid,
  Customized,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const LevelHUD = ({
  totalEmissions,
  currentOutdoorTemp,
  totalCost,
  totalEnergyConsumed,
  currentIndoorTemp,
  results,
  CustomBands,
  xDomain,
  ticks,
  handlePlay,
  simulationRunning,
  sliderRef,
  maxHeatingPower,
  heatingPower,
  onHeatingPowerChange,
  onSmallPipesChange,
  electricityPrices,
  temperatureData,
  currentHour,
  showEmission,
  isLevel4,
}) => {
  const smallPipeMax = Math.floor(maxHeatingPower / 3);
  const smallPipeStep = 1000;
  const timelineRef1 = useRef(null);
  const currentItemRef1 = useRef(null);
  const timelineRef2 = useRef(null);
  const currentItemRef2 = useRef(null);

  const getInitialSmallPipes = () => {
    const clamped = Math.max(0, Math.min(maxHeatingPower, heatingPower || 0));
    const base = Math.floor(clamped / 3);
    const remainder = clamped - base * 3;
    return [
      base + (remainder > 0 ? 1 : 0),
      base + (remainder > 1 ? 1 : 0),
      base,
    ];
  };
  const roundToStep = (value) =>
    Math.round(value / smallPipeStep) * smallPipeStep;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const axisToPipeValue = (axisValue) => {
    // Gamepad axes are in [-1, 1]. We invert so pushing the lever forward
    // (usually -1) gives higher pipe value.
    const normalized = (1 - axisValue) / 2;
    return roundToStep(clamp(normalized * smallPipeMax, 0, smallPipeMax));
  };
  const sumSmallPipes = (values) =>
    values.reduce((sum, value) => sum + value, 0);

  const [smallPipes, setSmallPipes] = useState(getInitialSmallPipes);
  const [isGamepadConnected, setIsGamepadConnected] = useState(false);
  const lastGamepadValuesRef = useRef([0, 0, 0]);

  const pollGamepad = () => {
    const gamepads = navigator.getGamepads ? navigator.getGamepads() : [];
    const firstConnected = Array.from(gamepads || []).find(Boolean);
    let rafId = null;

    if (!firstConnected || !firstConnected.axes) {
      setIsGamepadConnected(false);
      rafId = requestAnimationFrame(pollGamepad);
      return;
    }

    setIsGamepadConnected(true);
    const nextValues = [0, 1, 2].map((axisIndex) => {
      const axis = firstConnected.axes[axisIndex] ?? 1;
      return axisToPipeValue(axis);
    });

    const hasAnyChange = nextValues.some(
      (value, i) =>
        Math.abs(value - (lastGamepadValuesRef.current[i] ?? 0)) >
        1000,
    );

    if (hasAnyChange) {
      lastGamepadValuesRef.current = nextValues;
      setSmallPipes(nextValues);
      onSmallPipesChange(nextValues);
      onHeatingPowerChange(sumSmallPipes(nextValues));
    }

    rafId = requestAnimationFrame(pollGamepad);
  };
  const onConnect = () => setIsGamepadConnected(true);
  const onDisconnect = () => setIsGamepadConnected(false);

  useLayoutEffect(() => {
    const centerScroll = (itemRef) => {
      const child = itemRef.current;
      if (child) {
        child.scrollIntoView({
          behavior: "auto",
          block: "nearest",
          inline: "center",
        });
      }
    };

    // --- MODIFICATION START ---
    // Estimate your slide-in animation duration.
    // If it's, for example, 300ms (0.3s), add a bit more.
    const animationDuration = 300; // ms, adjust this to match your CSS animation/transition
    const delayForCentering = animationDuration + 50; // Add a small buffer

    const timerId = setTimeout(() => {
      centerScroll(currentItemRef1);
      centerScroll(currentItemRef2);
    }, delayForCentering);
    // --- MODIFICATION END ---

    return () => clearTimeout(timerId);
  }, [currentHour, electricityPrices, temperatureData]); // Dependencies remain the same

  useEffect(() => {
    window.addEventListener("gamepadconnected", onConnect);
    window.addEventListener("gamepaddisconnected", onDisconnect);
    let rafId = requestAnimationFrame(pollGamepad);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("gamepadconnected", onConnect);
      window.removeEventListener("gamepaddisconnected", onDisconnect);
    };
  }, []);

  useEffect(() => {
    const currentTotal = sumSmallPipes(smallPipes);
    const targetTotal = Math.round(heatingPower || 0);
    if (currentTotal !== targetTotal) {
      const clamped = Math.max(0, Math.min(maxHeatingPower, targetTotal));
      const base = Math.floor(clamped / 3);
      const remainder = clamped - base * 3;
      const nextValues = [
        base + (remainder > 0 ? 1 : 0),
        base + (remainder > 1 ? 1 : 0),
        base,
      ].map((v) => Math.min(v, smallPipeMax));
      setSmallPipes(nextValues);
      onSmallPipesChange(nextValues);
    }
  }, [heatingPower, maxHeatingPower, smallPipes, smallPipeMax, onSmallPipesChange]);

  const getGraphBackgroundColor = (temp) => {
    if (temp < 18) return "#00BFFF9";
    if (temp < 20) return "#87CEFA9";
    if (temp < 21.5) return "#7FFFD49";
    if (temp < 23) return "#FDFD969";
    if (temp < 24.5) return "#FFB3479";
    return "#FF696135";
  };

  return (
    <div
      className="slide-in-left-transition"
      style={{
        position: "absolute",
        bottom: "0",
        left: "0",
        right: "0",
        margin: "24px",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: "24px",
        }}
      >
        {showEmission && (
          <div style={{
            width: "71.7vw"
          }}>
            <div
              style={{
                width: "46vw",
                marginBottom: "15px",
                marginLeft: "auto",
              }}
            >
              <EmissionTimeline
                temperatureData={temperatureData}
                currentHour={currentHour}
                timelineRef={timelineRef2}
                currentItemRef={currentItemRef2}
              />
            </div>
          </div>
        )}
        <button
          onClick={handlePlay}
          // onTouchStart={handlePlay}
          className="play-button play-stop-button"
          style={{
            marginLeft: "auto",
          }}
        >
          {simulationRunning ? "PAUSE" : "PLAY"}
        </button>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div className="all-stats-container">
          <div className="stats-container" style={{ marginTop: "0" }}>
            <div className="stat-metric">
              <strong>External Temperature:</strong>
              <div className="stat-box">
                <div className="stats">{currentOutdoorTemp} °C</div>
                <img
                  src="/external_temperature.svg"
                  alt="Price level"
                  style={{ width: "45px", marginLeft: "12px" }}
                />
              </div>
            </div>
          </div>
          <div className="stats-container">
            <div className="stat-metric">
              <strong>Total Emissions:</strong>
              <div className="stat-box">
                <div className="stats">
                  {(totalEmissions / 1000).toFixed(1)} kg
                </div>
                <img
                  src="/CO2.svg"
                  alt="Price level"
                  style={{ width: "45px", marginLeft: "12px" }}
                />
              </div>
            </div>
          </div>
          <div className="stats-container">
            <div className="stat-metric">
              <strong>Energy Consumed:</strong>
              <div className="stat-box">
                <div className="stats">
                  {(totalEnergyConsumed / 1000).toFixed(2)} MWh
                </div>
                <img
                  src="/heating_energy.svg"
                  alt="Price level"
                  style={{ width: "45px", marginLeft: "12px" }}
                />
              </div>
            </div>
          </div>
          <div className="stats-container">
            <div className="stat-metric">
              <strong>Total Costs:</strong>
              <div className="stat-box">
                <div className="stats">{totalCost.toFixed(0)} Kr</div>
                <img
                  src="/cost.svg"
                  alt="Price level"
                  style={{ width: "45px", marginLeft: "12px" }}
                />
              </div>
            </div>
          </div>
        </div>
        <div
          style={{ display: "flex", flexDirection: "column", width: "46vw" }}
        >
          <>
            <div
              className="graph"
              style={{
                backgroundColor: getGraphBackgroundColor(currentIndoorTemp),
                transition: "background-color 0.5s ease",
                height: "100%",
                borderRadius: "19px",
                zIndex: "19999",
              }}
            >
              <ResponsiveContainer>
                <LineChart data={results}>
                  <defs>
                    <linearGradient
                      id="morningGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#FFBE00" stopOpacity={1} />
                      <stop offset="100%" stopColor="#FFA500" stopOpacity={1} />
                    </linearGradient>
                    <linearGradient
                      id="eveningGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#e4299c" stopOpacity={1} />
                      <stop offset="100%" stopColor="#FF69B4" stopOpacity={1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 5" />
                  <Customized component={CustomBands} />
                  <XAxis
                    dataKey="time"
                    type="number"
                    domain={xDomain}
                    ticks={ticks}
                    allowDataOverflow={true}
                    tickFormatter={(t) => `${Math.round(t * 24)}h`}
                    tickLine={false}
                    tick={{ fill: "#eef6ff" }}
                  />
                  <YAxis
                    domain={[16, 27]}
                    tick={{ fill: "#eef6ff" }}
                    ticks={[17, 20, 23, 26]}
                    tickFormatter={(c) => `${c}°C`}
                  />
                  <Tooltip
                    trigger="hover"
                    formatter={(value) =>
                      typeof value === "number"
                        ? `${value.toFixed(2)}°C`
                        : value
                    }
                    labelFormatter={(time) => `Hour: ${Math.round(time * 24)}h`}
                    contentStyle={{
                      backgroundColor: "rgba(228, 41, 156, 0.60)",
                      border: "3px solid #e4299c",
                      borderRadius: "10px",
                      color: "#eef6ff",
                      boxShadow: "0 8px 20px rgba(0,0,0,0.35)",
                    }}
                    itemStyle={{ color: "#eef6ff", fontWeight: 600 }}
                    labelStyle={{ color: "#eef6ff" }}
                    cursor={{ display: "none" }}
                  />
                  <Line
                    type="monotone"
                    dataKey="indoor"
                    isAnimationActive={false}
                    stroke="#e4299c"
                    strokeWidth={7}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    dot={false}
                    name="Indoor temperature"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            {electricityPrices && (
              <PriceTimeline
                electricityPrices={electricityPrices}
                currentHour={currentHour}
                timelineRef={timelineRef1}
                currentItemRef={currentItemRef1}
              />
            )}
          </>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
            textAlign: "center",
            color: "#eef6ff",
            zIndex: "10000",
            width: "22vw",
            marginLeft: "2vw",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "24px",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "24px",
              backgroundColor: "rgba(228, 41, 156, 0.25)",
              backdropFilter: "blur(15px)",
              WebkitBackdropFilter: "blur(15px)",
              borderRadius: "24px",
              border: "3px solid #e4299c",
              color: "#eef6ff",
              height: "100%",
              width: "100%",
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "16px",
                width: "100%",
                flex: "2 1 0",
              }}
            >
              <div
                style={{
                  height: "150px",
                  width: "56px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <input
                  type="range"
                  id={`pipe0`}
                  min="0"
                  ref={sliderRef}
                  max={maxHeatingPower}
                  step="1000"
                  value={heatingPower}
                  onChange={(e) => {
                    onHeatingPowerChange(Number(e.target.value));
                  }}
                  style={{
                    width: "150px",
                    transform: "rotate(-90deg)",
                    transformOrigin: "center",
                    margin: 0,
                    backgroundColor: "#EEF6FF",
                  }}
                />
              </div>
              <div
                className="heating-power"
                style={{ transform: "rotate(-2deg)" }}
              >
                {heatingPower / 1000} kW
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "14px",
                alignItems: "flex-end",
                justifyContent: "space-between",
                width: "100%",
                flex: "1 1 0",
              }}
            >
              <div
                style={{
                  width: "100%",
                  textAlign: "center",
                  marginBottom: "4px",
                  fontSize: "11px",
                  opacity: 0.8,
                }}
              >
                Throttle: {isGamepadConnected ? "connected" : "not connected"}
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "14px",
                  alignItems: "flex-end",
                  justifyContent: "space-between",
                  width: "100%",
                  flex: "1 1 0",
                }}
              >
                {[1, 2, 3]?.map((idx) => (
                  <div
                    key={`pipe${idx}`}
                    style={{
                      height: `90px`,
                      width: "52px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flex: 1,
                    }}
                  >
                    <input
                      type="range"
                      id={`pipe${idx}`}
                      min="0"
                      max={smallPipeMax}
                      step={smallPipeStep}
                      value={smallPipes[idx - 1]}
                      disabled
                      style={{
                        width: `90px`,
                        transform: "rotate(-90deg)",
                        transformOrigin: "center",
                        margin: 0,
                        backgroundColor: "#EEF6FF",
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LevelHUD;
